(function () {
  'use strict';

  if (typeof window === 'undefined' || !window.customElements || window.customElements.get('nl-command-select')) return;

  var CHECK_ICON = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEVRON_ICON = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="m7 9 5 5 5-5M7 15l5-5 5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var instanceId = 0;

  function upgradeProperty(el, prop) {
    if (Object.prototype.hasOwnProperty.call(el, prop)) {
      var value = el[prop];
      delete el[prop];
      el[prop] = value;
    }
  }

  class NlCommandSelect extends HTMLElement {
    static get observedAttributes() {
      return ['aria-invalid', 'aria-describedby', 'aria-required', 'disabled', 'loading'];
    }

    constructor() {
      super();
      instanceId += 1;
      this._instanceId = instanceId;
      this._value = '';
      this._options = [];
      this._open = false;
      this._activeIndex = -1;
      this._emptyResults = false;
      this._emptyQuery = '';
      this._built = false;
      this._portalScrimAnchor = null;
      this._portalPanelAnchor = null;
      this._portalWrapper = null;
      this._onDocumentClick = this._onDocumentClick.bind(this);
      this._syncVisualViewport = this._syncVisualViewport.bind(this);
      upgradeProperty(this, 'value');
      upgradeProperty(this, 'loading');
    }

    get value() {
      return this._value;
    }

    set value(next) {
      this._value = next == null ? '' : String(next);
      this._syncDisplay();
    }

    // Mirrors ResponsiveSelect.tsx isLoading: while true the trigger is
    // disabled and shows a pulsing skeleton instead of its label.
    get loading() {
      return this.hasAttribute('loading');
    }

    set loading(next) {
      if (next) this.setAttribute('loading', '');
      else this.removeAttribute('loading');
    }

    // Some fields (brand, model family) populate their options from a fetch
    // instead of Liquid-rendered <option>s, so the catalogue script rebuilds
    // this list at runtime rather than mutating light-DOM children directly.
    setOptions(list) {
      this._options = (list || []).map(function (item) {
        return {
          value: String(item.value),
          label: item.label != null ? String(item.label) : String(item.value),
          group: item.group || null,
          alwaysVisible: !!item.alwaysVisible,
        };
      });
      if (this._built) this._syncDisplay();
    }

    connectedCallback() {
      if (this._built) return;
      this._built = true;
      this._parseOptions();
      this._render();
      this._bind();
      var self = this;
      NlCommandSelect.observedAttributes.forEach(function (name) {
        if (self.hasAttribute(name)) self._proxyAttribute(name, self.getAttribute(name));
      });
      this._syncDisplay();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocumentClick, true);
      this._stopVisualViewportTracking();
      this._unportalFromBody();
    }

    attributeChangedCallback(name, _oldValue, newValue) {
      if (name === 'loading') {
        this._renderLoading(newValue != null);
        return;
      }
      this._proxyAttribute(name, newValue);
    }

    _proxyAttribute(name, value) {
      if (name === 'loading') {
        this._renderLoading(value != null);
        return;
      }
      if (!this._trigger) return;
      // Loading also disables the trigger, so removing the host's disabled
      // attribute must not re-enable it while a catalogue fetch is in flight.
      if (name === 'disabled' && value == null && this.hasAttribute('loading')) value = '';
      var targets = name === 'disabled' ? [this._trigger, this._search] : [this._trigger];
      targets.forEach(function (el) {
        if (value == null) el.removeAttribute(name);
        else el.setAttribute(name, value);
      });
    }

    // Mirrors ResponsiveSelect.tsx isLoading: while loading the trigger is
    // disabled and its label is swapped for a pulsing skeleton bar.
    _renderLoading(loading) {
      if (!this._trigger) return;
      if (loading) {
        this._trigger.setAttribute('disabled', '');
        this._search.setAttribute('disabled', '');
      } else {
        this._proxyAttribute('disabled', this.getAttribute('disabled'));
      }
      if (!this._labelSpan) return;
      if (loading) {
        this._labelSpan.classList.add('nl-command-value--loading');
        this._labelSpan.textContent = '';
        var skeleton = document.createElement('span');
        skeleton.className = 'nl-command-skeleton';
        skeleton.setAttribute('aria-hidden', 'true');
        this._labelSpan.appendChild(skeleton);
      } else {
        this._labelSpan.classList.remove('nl-command-value--loading');
        this._syncDisplay();
      }
    }

    _parseOptions() {
      var placeholder = null;
      var options = [];
      Array.prototype.forEach.call(this.children, function (child) {
        if (child.tagName === 'OPTION') {
          if (child.value === '') {
            if (placeholder == null) placeholder = child.textContent;
            return;
          }
          options.push({ value: child.value, label: child.textContent, group: null, alwaysVisible: child.hasAttribute('data-always-visible') });
        } else if (child.tagName === 'OPTGROUP') {
          var groupLabel = child.getAttribute('label') || '';
          Array.prototype.forEach.call(child.children, function (opt) {
            if (opt.tagName !== 'OPTION') return;
            if (opt.value === '') {
              if (placeholder == null) placeholder = opt.textContent;
              return;
            }
            options.push({ value: opt.value, label: opt.textContent, group: groupLabel, alwaysVisible: opt.hasAttribute('data-always-visible') });
          });
        }
      });
      this._options = options;
      this._placeholder = placeholder || this.getAttribute('placeholder') || 'Select…';
      this._sheetTitle = this._resolveSheetTitle();
      this.textContent = '';
    }

    _resolveSheetTitle() {
      var field = this.closest('label.nl-field, label');
      var span = field && field.querySelector(':scope > span:first-child');
      return (span && span.textContent.trim()) || this._placeholder || 'Select an option';
    }

    _render() {
      this.classList.add('nl-command-select');

      // The field id stays on the host, not the trigger: readControl/writeControl
      // and every getElementById(fieldId) caller expect the element carrying
      // data-field to be the one with .value, so a native-select swap can't
      // move id to an inner element the way a plain accessibility fix would.
      var idBase = this.id || 'nl-command-select-' + this._instanceId;

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'nl-command-trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', this._sheetTitle);

      var labelSpan = document.createElement('span');
      labelSpan.className = 'nl-command-value';
      trigger.appendChild(labelSpan);

      var chevron = document.createElement('span');
      chevron.className = 'nl-command-chevron';
      chevron.setAttribute('aria-hidden', 'true');
      chevron.innerHTML = CHEVRON_ICON;
      trigger.appendChild(chevron);

      var scrim = document.createElement('div');
      scrim.className = 'nl-command-scrim';
      scrim.hidden = true;

      var panel = document.createElement('div');
      panel.className = 'nl-command-panel';
      panel.hidden = true;

      var sheetHeader = document.createElement('div');
      sheetHeader.className = 'nl-command-sheet-header';
      var sheetTitle = document.createElement('p');
      sheetTitle.className = 'nl-command-sheet-title';
      sheetTitle.textContent = this._sheetTitle;
      sheetHeader.appendChild(sheetTitle);
      var sheetClose = document.createElement('button');
      sheetClose.type = 'button';
      sheetClose.className = 'nl-command-sheet-close';
      sheetClose.setAttribute('aria-label', 'Close');
      sheetClose.textContent = '✕';
      sheetHeader.appendChild(sheetClose);
      panel.appendChild(sheetHeader);

      var search = document.createElement('input');
      search.type = 'text';
      search.className = 'nl-command-search';
      search.setAttribute('role', 'combobox');
      search.setAttribute('aria-autocomplete', 'list');
      search.setAttribute('aria-expanded', 'false');
      search.setAttribute('autocomplete', 'off');
      search.setAttribute('aria-label', this.getAttribute('search-label') || 'Search options');
      panel.appendChild(search);

      var list = document.createElement('ul');
      list.className = 'nl-command-list';
      list.id = idBase + '-listbox';
      list.setAttribute('role', 'listbox');
      var empty = document.createElement('div');
      empty.className = 'nl-command-empty';
      empty.hidden = true;
      var emptyMessage = document.createElement('p');
      emptyMessage.className = 'nl-command-empty__message';
      emptyMessage.setAttribute('role', 'status');
      emptyMessage.setAttribute('aria-live', 'polite');
      emptyMessage.textContent = 'No items found.';
      empty.appendChild(emptyMessage);
      var emptyAction = document.createElement('button');
      emptyAction.type = 'button';
      emptyAction.className = 'nl-command-empty__action';
      emptyAction.hidden = true;
      empty.appendChild(emptyAction);
      panel.appendChild(empty);
      panel.appendChild(list);

      trigger.setAttribute('aria-controls', list.id);
      search.setAttribute('aria-controls', list.id);

      this.appendChild(scrim);
      this.appendChild(trigger);
      this.appendChild(panel);

      this._idBase = idBase;
      this._trigger = trigger;
      this._labelSpan = labelSpan;
      this._scrim = scrim;
      this._panel = panel;
      this._search = search;
      this._list = list;
      this._empty = empty;
      this._emptyMessage = emptyMessage;
      this._emptyAction = emptyAction;
      this._sheetClose = sheetClose;

      this._renderList('');
    }

    _bind() {
      var self = this;

      this._trigger.addEventListener('click', function () {
        if (self._open) self._closePanel(true);
        else self._openPanel();
      });

      this._trigger.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          self._openPanel();
        }
      });

      this._search.addEventListener('input', function () {
        self._renderList(self._search.value);
        self._setActive(self._visibleItems.length ? 0 : -1);
      });

      this._search.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          self._moveActive(1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          self._moveActive(-1);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          var item = self._visibleItems[self._activeIndex];
          if (item) self._selectValue(item.getAttribute('data-value'));
        } else if (event.key === 'Escape') {
          event.preventDefault();
          self._closePanel(true);
        } else if (event.key === 'Tab') {
          self._closePanel(false);
        }
      });

      this._list.addEventListener('click', function (event) {
        var item = event.target.closest('.nl-command-item');
        if (!item) return;
        self._selectValue(item.getAttribute('data-value'));
      });

      this._scrim.addEventListener('click', function () {
        self._closePanel(true);
      });

      this._sheetClose.addEventListener('click', function () {
        self._closePanel(true);
      });

      this._emptyAction.addEventListener('click', function () {
        var value = self.getAttribute('empty-search-action-value');
        if (value) self._selectValue(value);
      });

      // The <label for="…"> wraps this whole element, so every click inside
      // it (trigger, list items, scrim, sheet close) also bubbles up to the
      // label. Only treat it as "clicked the label itself" when the click
      // didn't originate from something this component already handled.
      // composedPath() is used instead of contains(event.target): selecting
      // an item rebuilds the list (removing the clicked <li> from the tree)
      // before this listener runs, so a live containment check would already
      // find the target detached and wrongly treat the click as external.
      var label = this._idBase && document.querySelector('label[for="' + this._idBase + '"]');
      if (label) {
        label.addEventListener('click', function (event) {
          var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
          if (path.indexOf(self) !== -1) return;
          event.preventDefault();
          self._openPanel();
        });
      }
    }

    _renderList(filter) {
      var self = this;
      var searchTerm = filter.trim();
      var query = searchTerm.toLowerCase();
      var emptySearchMessage = this.getAttribute('empty-search-message');
      var emptySearchDescription = this.getAttribute('empty-search-description');
      var emptySearchAction = this.getAttribute('empty-search-action');
      var emptySearchActionValue = this.getAttribute('empty-search-action-value');
      var hidePinnedWhileSearching = !!emptySearchActionValue;
      this._list.textContent = '';
      var currentGroup;
      var visibleItems = [];

      this._options.forEach(function (option) {
        var matches = !query || (option.alwaysVisible && !hidePinnedWhileSearching) ||
          option.label.toLowerCase().indexOf(query) !== -1;
        if (!matches) return;
        if (option.group !== currentGroup) {
          currentGroup = option.group;
          if (option.group) {
            var heading = document.createElement('li');
            heading.className = 'nl-command-group-label';
            heading.setAttribute('role', 'presentation');
            heading.textContent = option.group;
            self._list.appendChild(heading);
          }
        }

        var isSelected = option.value === self._value;
        var item = document.createElement('li');
        item.className = 'nl-command-item';
        item.id = self._idBase + '-option-' + visibleItems.length;
        item.setAttribute('role', 'option');
        item.setAttribute('data-value', option.value);
        item.setAttribute('aria-selected', isSelected ? 'true' : 'false');

        var check = document.createElement('span');
        check.className = 'nl-command-check';
        check.setAttribute('aria-hidden', 'true');
        if (isSelected) check.innerHTML = CHECK_ICON;
        item.appendChild(check);

        var label = document.createElement('span');
        label.textContent = option.label;
        item.appendChild(label);

        // alwaysVisible options are the fallback escape hatches; mark
        // them so CSS can pin them to the bottom edge of the scrolling list
        // instead of letting them sink beneath hundreds of brands.
        if (option.alwaysVisible) item.classList.add('nl-command-item--pinned');

        self._list.appendChild(item);
        visibleItems.push(item);
      });

      this._visibleItems = visibleItems;
      this._empty.hidden = visibleItems.length > 0;
      if (query && visibleItems.length === 0 && emptySearchMessage) {
        this._emptyMessage.textContent = emptySearchMessage + ' "' + searchTerm + '".' +
          (emptySearchDescription ? ' ' + emptySearchDescription : '');
        this._emptyAction.textContent = emptySearchAction || '';
        this._emptyAction.hidden = !emptySearchAction || !emptySearchActionValue;
      } else {
        this._emptyMessage.textContent = 'No items found.';
        this._emptyAction.textContent = '';
        this._emptyAction.hidden = true;
      }
      this._setEmptyResultsState(Boolean(query && visibleItems.length === 0), query);
      this._activeIndex = -1;
      this._search.removeAttribute('aria-activedescendant');

    }

    _setActive(index) {
      if (this._visibleItems[this._activeIndex]) {
        this._visibleItems[this._activeIndex].classList.remove('nl-command-item--active');
      }
      this._activeIndex = index;
      var item = this._visibleItems[index];
      if (!item) {
        this._search.removeAttribute('aria-activedescendant');
        return;
      }
      item.classList.add('nl-command-item--active');
      this._search.setAttribute('aria-activedescendant', item.id);
      if (typeof item.scrollIntoView === 'function') item.scrollIntoView({ block: 'nearest' });
    }

    _moveActive(delta) {
      var count = this._visibleItems.length;
      if (!count) return;
      var next = this._activeIndex < 0 ? 0 : (this._activeIndex + delta + count) % count;
      this._setActive(next);
    }

    _openPanel() {
      // Loading disables the trigger, but a wrapping <label for> click still
      // reaches us — don't open a half-populated list mid-fetch.
      if (this._open || this.hasAttribute('loading')) return;
      this._open = true;
      this.classList.add('is-open');
      this._panel.hidden = false;
      this._scrim.hidden = false;
      this._trigger.setAttribute('aria-expanded', 'true');
      this._search.setAttribute('aria-expanded', 'true');
      this._search.value = '';
      this._renderList('');
      this._portalToBody();
      this._startVisualViewportTracking();
      if (!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches)) this._search.focus();
      document.addEventListener('click', this._onDocumentClick, true);
    }

    _closePanel(focusTrigger) {
      if (!this._open) {
        this._setEmptyResultsState(false, '');
        return;
      }
      this._open = false;
      this.classList.remove('is-open');
      this._panel.hidden = true;
      this._scrim.hidden = true;
      this._trigger.setAttribute('aria-expanded', 'false');
      this._search.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this._onDocumentClick, true);
      this._stopVisualViewportTracking();
      this._unportalFromBody();
      this._setEmptyResultsState(false, '');
      if (focusTrigger) this._trigger.focus();
    }

    _setEmptyResultsState(empty, query) {
      if (this._emptyResults === empty && this._emptyQuery === query) return;
      this._emptyResults = empty;
      this._emptyQuery = query;
      this.dispatchEvent(new CustomEvent('nl-command-select-empty-change', {
        bubbles: true,
        detail: { empty: empty, query: query }
      }));
    }

    _isMobileSheet() {
      return !!(window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
    }

    _portalToBody() {
      // Keep the form control in its original tree so input/change events
      // still reach the listing root; only the fixed UI needs to escape the
      // scrolling form containing block.
      if (!this._isMobileSheet() || this._portalWrapper || !this.parentNode || !document.body) return;

      this._portalScrimAnchor = document.createComment('nl-command-select-scrim-portal');
      this._portalPanelAnchor = document.createComment('nl-command-select-panel-portal');
      this.insertBefore(this._portalScrimAnchor, this._scrim);
      this.insertBefore(this._portalPanelAnchor, this._panel);

      var wrapper = document.createElement('div');
      wrapper.className = 'ink ink-new-listing nl-command-select-portal';
      document.body.appendChild(wrapper);

      this._portalWrapper = wrapper;
      wrapper.appendChild(this._scrim);
      wrapper.appendChild(this._panel);
    }

    _unportalFromBody() {
      if (!this._portalWrapper) return;

      if (this._portalScrimAnchor && this._portalScrimAnchor.parentNode) {
        this._portalScrimAnchor.parentNode.insertBefore(this._scrim, this._portalScrimAnchor);
      }
      if (this._portalPanelAnchor && this._portalPanelAnchor.parentNode) {
        this._portalPanelAnchor.parentNode.insertBefore(this._panel, this._portalPanelAnchor);
      }

      if (this._portalWrapper.parentNode) this._portalWrapper.remove();
      if (this._portalScrimAnchor) this._portalScrimAnchor.remove();
      if (this._portalPanelAnchor) this._portalPanelAnchor.remove();
      this._portalWrapper = null;
      this._portalScrimAnchor = null;
      this._portalPanelAnchor = null;
    }

    _startVisualViewportTracking() {
      var viewport = window.visualViewport;
      if (!this._isMobileSheet() || !viewport) return;
      this._visualViewport = viewport;
      this._preferredSheetHeight = Math.round(viewport.height * 0.85);
      viewport.addEventListener('resize', this._syncVisualViewport);
      viewport.addEventListener('scroll', this._syncVisualViewport);
      this._syncVisualViewport();
    }

    _syncVisualViewport() {
      if (!this._visualViewport || !this._panel) return;
      var viewportHeight = Math.round(this._visualViewport.height);
      // Preserve the pre-keyboard sheet height so its opaque surface continues
      // behind iOS's translucent keyboard and accessory controls. Shrinking the
      // panel itself exposes the listing page underneath those controls; only
      // the list's scrollport should follow the reduced visual viewport.
      var sheetHeight = this._preferredSheetHeight;
      var sheetTop = Math.max(0, viewportHeight - sheetHeight);
      var offsetTop = (this._visualViewport.offsetTop || 0) + 'px';
      var panelChromeHeight = Array.prototype.reduce.call(this._panel.children, function (height, child) {
        if (child === this._list || child.hidden) return height;
        return height + child.offsetHeight;
      }.bind(this), 0);
      var visiblePanelHeight = Math.max(0, viewportHeight - sheetTop);
      var listMaxHeight = Math.max(0, visiblePanelHeight - panelChromeHeight) + 'px';
      this.style.setProperty('--nl-command-vv-offset-top', offsetTop);
      this.style.setProperty('--nl-command-list-max-height', listMaxHeight);
      if (this._portalWrapper) {
        this._portalWrapper.style.setProperty('--nl-command-vv-offset-top', offsetTop);
        this._portalWrapper.style.setProperty('--nl-command-list-max-height', listMaxHeight);
      }
      this._panel.style.top = sheetTop + 'px';
      this._panel.style.bottom = 'auto';
      this._panel.style.height = sheetHeight + 'px';
    }

    _stopVisualViewportTracking() {
      if (this._visualViewport) {
        this._visualViewport.removeEventListener('resize', this._syncVisualViewport);
        this._visualViewport.removeEventListener('scroll', this._syncVisualViewport);
      }
      this._visualViewport = null;
      this._preferredSheetHeight = null;
      this.style.removeProperty('--nl-command-vv-offset-top');
      this.style.removeProperty('--nl-command-list-max-height');
      if (this._portalWrapper) {
        this._portalWrapper.style.removeProperty('--nl-command-vv-offset-top');
        this._portalWrapper.style.removeProperty('--nl-command-list-max-height');
      }
      if (!this._panel) return;
      this._panel.style.removeProperty('top');
      this._panel.style.removeProperty('bottom');
      this._panel.style.removeProperty('height');
    }

    _selectValue(value) {
      var changed = value !== this._value;
      this._value = value;
      this._syncDisplay();
      if (changed) {
        this.dispatchEvent(new Event('input', { bubbles: true }));
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this._closePanel(true);
    }

    _syncDisplay() {
      if (!this._labelSpan) return;
      // The skeleton owns the label while loading; setOptions()/value syncs
      // land back here once loading clears.
      if (this.hasAttribute('loading')) return;
      var self = this;
      var match = this._options.filter(function (option) {
        return option.value === self._value;
      })[0];
      this._labelSpan.textContent = match ? match.label : this._placeholder;
      this._labelSpan.classList.toggle('nl-command-value--placeholder', !match);
      // Rebuilding the <li> list (hundreds of them for a field like brand) is
      // wasted work while the panel is hidden — selecting one catalogue field
      // re-syncs every other one via setOptions()/value=, so skipping this
      // keeps a single selection from re-rendering five invisible lists. The
      // list is always rebuilt fresh in _openPanel(), so nothing goes stale.
      if (this._open) this._renderList(this._search ? this._search.value : '');
    }

    _onDocumentClick(event) {
      var inPortal = this._portalWrapper && this._portalWrapper.contains(event.target);
      if (!this.contains(event.target) && !inPortal) this._closePanel(false);
    }
  }

  window.customElements.define('nl-command-select', NlCommandSelect);
})();
